import React, {useRef, useState} from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {alpha, useTheme} from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import EventIcon from '@mui/icons-material/Event';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import RedoIcon from '@mui/icons-material/Redo';
import TodayIcon from '@mui/icons-material/Today';
import UndoIcon from '@mui/icons-material/Undo';
import NewStatusPicker from 'crqlar-react-common/dist/components/NewStatusPicker';
import CountryLanguagePicker from 'crqlar-react-common/dist/components/inputs/CountryLanguagePicker';
import {useTranslation} from 'react-i18next';

import {SingleSelect} from '../../../../../common/newSelect';
import type {SelectOption} from '../../../../../common/newSelect';
import {
    IfConditionGroup,
    IfConditionRule,
    IfFieldOption,
    IfLogicOperator,
    NO_VALUE_OPERATORS,
    TODAY_VALUE,
    codeComparisonValue,
    emptyRule,
    fieldTypeOf,
    groupIsEmpty,
    newBox,
    operatorsForType
} from '../../../config/ifConditions';

/**
 * The If rules: the audience builder's fixed shape over values earlier steps produce. Rules in a box are AND,
 * boxes are OR, and a sub-group is the only ANY/ALL choice. Each row is one grid line: field › operator › value › ⋮.
 */

const ROW_HEIGHT = 40;
const HISTORY_LIMIT = 50;
const ROW_COLUMNS = 'minmax(0, 1.8fr) minmax(0, 1fr) minmax(0, 1.2fr) 34px';

interface RuleRowProps {
    rule: IfConditionRule;
    setRule: (rule: IfConditionRule) => void;
    onDelete: () => void;
    onDuplicate: () => void;
    fieldOptions: IfFieldOption[];
    readOnly: boolean;
    autoFocus: boolean;
}

const RuleRow = ({rule, setRule, onDelete, onDuplicate, fieldOptions, readOnly, autoFocus}: RuleRowProps) => {
    const {t} = useTranslation();
    const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
    const closeMenu = () => setMenuAnchor(null);

    const fieldType = fieldTypeOf(rule.field, fieldOptions);
    const operators = operatorsForType(fieldType);
    const valueless = !!rule.operator && NO_VALUE_OPERATORS.includes(rule.operator);
    const relativeDate = ['older_than_days', 'within_last_days'].includes(rule.operator);
    const isToday = rule.value === TODAY_VALUE && !relativeDate;

    // A stored field the steps above no longer produce stays visible as SingleSelect's unknown value.
    const selected = fieldOptions.find(option => option.value === rule.field);
    const fieldSelectOptions: SelectOption[] = fieldOptions.map(option => ({
        value: option.value,
        label: option.label,
        group: option.group
    }));

    const valueInput = () => {
        if (fieldType === 'contactHistory' || fieldType === 'reservationContactHistory') {
            const period = rule.operator === 'contact_fewer_than_in_days';
            const number = (label: string, value: any, max: number, change: (value: number | '') => void) => (
                <TextField size={'small'} fullWidth type={'number'} label={t(label)} disabled={readOnly}
                           value={value ?? ''} inputProps={{min: 1, max, step: 1}}
                           onChange={event => change(event.target.value === '' ? '' : Number(event.target.value))}
                           sx={{'& .MuiOutlinedInput-root': {height: ROW_HEIGHT}}}/>
            );
            if (period) return <Stack direction={'row'} gap={1}>
                {number('Times', rule.value?.count, 100, count => setRule({...rule, value: {...rule.value, count}}))}
                {number('Days', rule.value?.days, 3650, days => setRule({...rule, value: {...rule.value, days}}))}
            </Stack>;
            const days = rule.operator === 'contact_not_received_in_days';
            return number(days ? 'Days' : 'Times', rule.value, days ? 3650 : 100, value => setRule({...rule, value}));
        }
        if (selected?.valueKind
            && (rule.value == null || typeof rule.value === 'string')) {
            return (
                <CountryLanguagePicker
                    kind={selected.valueKind === 'language' ? 'language' : 'country'}
                    label={t(selected.valueKind === 'language' ? 'Language' : selected.valueKind === 'nationality' ? 'Nationality' : 'Country')}
                    value={rule.value ?? ''}
                    onChange={value => setRule({...rule, value: codeComparisonValue(value, rule.value ?? '')})}
                    disabled={readOnly}
                />
            );
        }

        if (fieldType === 'boolean') {
            return (
                <SingleSelect
                    placeholder={t('Value')}
                    value={rule.value === true ? 'true' : 'false'}
                    options={[{value: 'true', label: t('True')}, {value: 'false', label: t('False')}]}
                    onChange={next => setRule({...rule, value: next === 'true'})}
                    disabled={readOnly}
                    disableClearable
                />
            );
        }

        // A closed value set is a select only for equals/not_equals; other operators ("contains") stay text.
        const valueOptions = selected?.options ?? [];
        if (valueOptions.length && (rule.operator === 'equals' || rule.operator === 'not_equals')) {
            const storedValue = rule.value == null ? '' : String(rule.value);

            return (
                <SingleSelect
                    placeholder={t('Value')}
                    value={storedValue}
                    options={valueOptions.map(option => ({value: option.value, label: t(option.label)}))}
                    unknownLabel={storedValue}
                    onChange={next => setRule({...rule, value: next})}
                    disabled={readOnly}
                />
            );
        }

        if (isToday) {
            return (
                <TextField
                    size={'small'}
                    hiddenLabel
                    disabled
                    fullWidth
                    value={t('Today')}
                    sx={{'& .MuiOutlinedInput-root': {height: ROW_HEIGHT}}}
                />
            );
        }

        return (
            <TextField
                size={'small'}
                hiddenLabel
                fullWidth
                disabled={readOnly || !rule.field}
                type={(fieldType === 'number' || ['older_than_days', 'within_last_days'].includes(rule.operator)) ? 'number' : 'text'}
                placeholder={t('Value')}
                value={rule.value ?? ''}
                onChange={event => setRule({
                    ...rule,
                    value: (fieldType === 'number' || ['older_than_days', 'within_last_days'].includes(rule.operator))
                        ? (event.target.value === '' ? '' : Number(event.target.value))
                        : event.target.value
                })}
                sx={{'& .MuiOutlinedInput-root': {height: ROW_HEIGHT}}}
            />
        );
    };

    return (
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: {xs: 'minmax(0, 1fr) minmax(0, 1fr) 34px', sm: ROW_COLUMNS},
                columnGap: 1,
                rowGap: 1,
                alignItems: 'center',
                minHeight: ROW_HEIGHT
            }}
        >
            <Box sx={{minWidth: 0, gridColumn: {xs: '1 / -1', sm: 'auto'}}}>
                <SingleSelect
                    placeholder={t('Field')}
                    value={rule.field}
                    options={fieldSelectOptions}
                    unknownLabel={rule.field}
                    onChange={next => {
                        const option = fieldOptions.find(candidate => candidate.value === next);
                        setRule({
                            ...rule,
                            field: next,
                            // The operators depend on the type, so the old one rarely survives the swap.
                            operator: option ? operatorsForType(option.type)[0]?.value ?? '' : '',
                            value: option?.type === 'contactHistory' ? 10 : option?.type === 'reservationContactHistory' ? 1 : option?.type === 'boolean' ? false : ''
                        });
                    }}
                    disabled={readOnly}
                    autoFocus={autoFocus}
                />
            </Box>

            {/* A stored operator the field type no longer allows stays readable; the engine ignores such a rule. */}
            <Box sx={{minWidth: 0, gridColumn: valueless ? 'span 2' : 'auto'}}>
                <SingleSelect
                    placeholder={t('Operator')}
                    value={rule.operator || ''}
                    options={operators.map(operator => ({value: operator.value, label: t(operator.label)}))}
                    unknownLabel={rule.operator}
                    onChange={next => {
                        if (fieldType === 'contactHistory' || fieldType === 'reservationContactHistory') {
                            setRule({...rule, operator: next, value: next === 'contact_not_received_in_days' ? 10
                                : next === 'contact_fewer_than' ? 1 : next === 'contact_fewer_than_in_days' ? {count: 1, days: 10} : undefined});
                            return;
                        }
                        if (fieldType === 'boolean' && next === 'equals') {
                            setRule({...rule, operator: next, value: typeof rule.value === 'boolean' ? rule.value : false});
                            return;
                        }
                        setRule({...rule, operator: next, ...((NO_VALUE_OPERATORS.includes(next) || ['older_than_days', 'within_last_days'].includes(next)) ? {value: ''} : {})});
                    }}
                    disabled={readOnly || !rule.field}
                    disableClearable
                />
            </Box>

            {!valueless && <Box sx={{minWidth: 0}}>{valueInput()}</Box>}

            <IconButton
                size={'small'}
                aria-label={t('Rule options')}
                disabled={readOnly}
                onClick={event => setMenuAnchor(event.currentTarget)}
            >
                <MoreVertIcon/>
            </IconButton>

            <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={closeMenu}>
                {fieldType === 'date' && !valueless && !relativeDate && (
                    <MenuItem onClick={() => {
                        setRule({...rule, value: isToday ? '' : TODAY_VALUE});
                        closeMenu();
                    }}>
                        <ListItemIcon>{isToday ? <EventIcon fontSize={'small'}/> : <TodayIcon fontSize={'small'}/>}</ListItemIcon>
                        <ListItemText>{isToday ? t('Compare with a specific date') : t('Compare with today')}</ListItemText>
                    </MenuItem>
                )}
                <MenuItem onClick={() => {
                    onDuplicate();
                    closeMenu();
                }}>
                    <ListItemIcon><ContentCopyIcon fontSize={'small'}/></ListItemIcon>
                    <ListItemText>{t('Duplicate')}</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => {
                    onDelete();
                    closeMenu();
                }} sx={{color: 'error.main'}}>
                    <ListItemIcon><DeleteIcon fontSize={'small'} color={'error'}/></ListItemIcon>
                    <ListItemText>{t('Delete rule')}</ListItemText>
                </MenuItem>
            </Menu>
        </Box>
    );
};

interface RuleBoxProps {
    group: IfConditionGroup;
    setGroup: (group: IfConditionGroup) => void;
    onEmpty: () => void;
    fieldOptions: IfFieldOption[];
    readOnly: boolean;
    depth: number;
    focusFirstRule?: boolean;
}

const RuleBox = ({group, setGroup, onEmpty, fieldOptions, readOnly, depth, focusFirstRule}: RuleBoxProps) => {
    const {t} = useTranslation();
    const theme = useTheme();
    const [focusIndex, setFocusIndex] = useState<number | null>(focusFirstRule ? 0 : null);
    const [focusSubIndex, setFocusSubIndex] = useState<number | null>(null);
    const isSubGroup = depth > 0;
    const operator = group.operator ?? 'AND';
    const rules = group.rules ?? [];
    const subGroups = group.groups ?? [];

    const update = (next: IfConditionGroup) => groupIsEmpty(next) ? onEmpty() : setGroup(next);
    const setRules = (nextRules: IfConditionRule[]) => update({...group, rules: nextRules});
    const setSubGroups = (nextGroups: IfConditionGroup[]) => update({...group, groups: nextGroups});

    const addRule = () => {
        setFocusIndex(rules.length);
        setGroup({...group, rules: [...rules, emptyRule()]});
    };

    const addSubGroup = () => {
        setFocusSubIndex(subGroups.length);
        setGroup({...group, groups: [...subGroups, {operator: 'OR', rules: [emptyRule()]}]});
    };

    const body = (
        <>
            <Stack gap={1}>
                {rules.map((rule, index) => (
                    <RuleRow
                        key={`rule-${index}`}
                        rule={rule}
                        autoFocus={focusIndex === index}
                        setRule={nextRule => {
                            const nextRules = [...rules];
                            nextRules[index] = nextRule;
                            setRules(nextRules);
                        }}
                        onDelete={() => setRules(rules.filter((_, candidate) => candidate !== index))}
                        onDuplicate={() => {
                            const nextRules = [...rules];
                            nextRules.splice(index + 1, 0, {...rule});
                            setRules(nextRules);
                        }}
                        fieldOptions={fieldOptions}
                        readOnly={readOnly}
                    />
                ))}

                {subGroups.map((subGroup, index) => (
                    <RuleBox
                        key={`sub-${index}`}
                        group={subGroup}
                        depth={depth + 1}
                        focusFirstRule={focusSubIndex === index}
                        setGroup={nextSub => {
                            const nextGroups = [...subGroups];
                            nextGroups[index] = nextSub;
                            setSubGroups(nextGroups);
                        }}
                        onEmpty={() => setSubGroups(subGroups.filter((_, candidate) => candidate !== index))}
                        fieldOptions={fieldOptions}
                        readOnly={readOnly}
                    />
                ))}
            </Stack>

            {!readOnly && (
                <Stack direction={'row'} gap={1} sx={{mt: 1}}>
                    <Button size={'small'} variant={'text'} startIcon={<AddIcon/>} onClick={addRule}>
                        {t('Add rule')}
                    </Button>
                    <Button size={'small'} variant={'text'} startIcon={<AddIcon/>} onClick={addSubGroup}>
                        {t('Add sub-group')}
                    </Button>
                </Stack>
            )}
        </>
    );

    if (isSubGroup) {
        return (
            <Box
                sx={{
                    p: 1.5,
                    borderRadius: 1,
                    border: `1px solid ${theme.palette.divider}`,
                    borderLeft: `4px solid ${theme.palette.warning.main}`,
                    bgcolor: alpha(theme.palette.warning.main, 0.04)
                }}
            >
                <Stack direction={'row'} alignItems={'center'} gap={1} sx={{mb: 1}}>
                    <NewStatusPicker<IfLogicOperator>
                        value={operator}
                        disabled={readOnly}
                        ariaLabel={t('Match')}
                        onChange={next => setGroup({...group, operator: next})}
                        options={[
                            {value: 'OR', label: t('Any of the following')},
                            {value: 'AND', label: t('All of the following')}
                        ]}
                    />
                </Stack>
                {body}
            </Box>
        );
    }

    return (
        <Paper variant={'outlined'} sx={{p: 1.5}}>
            <Typography variant={'subtitle2'} color={'text.secondary'} sx={{mb: 1}}>
                {t('WHERE')}
            </Typography>
            {body}
        </Paper>
    );
};

/** Snapshot undo/redo over the boxes, as in the audience builder. */
const useGroupHistory = (groups: IfConditionGroup[], setGroups: (groups: IfConditionGroup[]) => void) => {
    const past = useRef<IfConditionGroup[][]>([]);
    const future = useRef<IfConditionGroup[][]>([]);
    const [, rerender] = useState(0);
    const touch = () => rerender(count => count + 1);

    const commit = (next: IfConditionGroup[]) => {
        past.current = [...past.current.slice(-(HISTORY_LIMIT - 1)), groups];
        future.current = [];
        touch();
        setGroups(next);
    };

    const undo = () => {
        const previous = past.current.pop();
        if (!previous) return;
        future.current.push(groups);
        setGroups(previous);
        touch();
    };

    const redo = () => {
        const next = future.current.pop();
        if (!next) return;
        past.current.push(groups);
        setGroups(next);
        touch();
    };

    return {commit, undo, redo, canUndo: past.current.length > 0, canRedo: future.current.length > 0};
};

interface IfConditionRulesProps {
    groups: IfConditionGroup[];
    setGroups: (groups: IfConditionGroup[]) => void;
    fieldOptions: IfFieldOption[];
    readOnly?: boolean;
    emptyText?: string;
}

const IfConditionRules = ({groups, setGroups, fieldOptions, readOnly = false, emptyText}: IfConditionRulesProps) => {
    const {t} = useTranslation();
    const history = useGroupHistory(groups, setGroups);
    const hasRules = groups.length > 0;

    const onKeyDown = (event: React.KeyboardEvent) => {
        if (readOnly || event.defaultPrevented || event.repeat) return;
        if (!(event.metaKey || event.ctrlKey) || event.key.toLowerCase() !== 'z') return;
        event.preventDefault();
        event.stopPropagation();
        if (event.shiftKey) history.redo();
        else history.undo();
    };

    return (
        <Stack gap={1.5} onKeyDown={onKeyDown}>
            <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'} gap={1}>
                <Typography variant={'subtitle1'} sx={{fontWeight: 600}}>
                    {hasRules ? t('Rules — matching any box') : t('Rules')}
                </Typography>

                <Stack direction={'row'} alignItems={'center'} gap={1}>
                    <IconButton aria-label={t('Undo')} disabled={readOnly || !history.canUndo} onClick={history.undo}>
                        <UndoIcon/>
                    </IconButton>
                    <IconButton aria-label={t('Redo')} disabled={readOnly || !history.canRedo} onClick={history.redo}>
                        <RedoIcon/>
                    </IconButton>
                </Stack>
            </Stack>

            {!hasRules ? (
                <Paper variant={'outlined'} sx={{p: 2}}>
                    <Typography variant={'body2'} color={'text.secondary'}>
                        {emptyText || t('No rules yet. Compare a value an earlier step produced with something you expect — the run takes the yes branch when the rules match.')}
                    </Typography>
                    {!readOnly && (
                        <Stack direction={'row'} sx={{mt: 1.5}}>
                            <Button
                                variant={'text'}
                                color={'inherit'}
                                startIcon={<AddIcon/>}
                                onClick={() => history.commit([newBox()])}
                                sx={{color: 'text.secondary'}}
                            >
                                {t('Add a rule by hand')}
                            </Button>
                        </Stack>
                    )}
                </Paper>
            ) : (
                <Stack gap={1.5}>
                    {groups.map((group, index) => (
                        <React.Fragment key={`box-${index}`}>
                            {index > 0 && (
                                <Typography
                                    variant={'body2'}
                                    align={'center'}
                                    color={'success.main'}
                                    sx={{fontWeight: 600, letterSpacing: '0.08em'}}
                                >
                                    {t('OR — alternatively')}
                                </Typography>
                            )}
                            <RuleBox
                                group={group}
                                depth={0}
                                setGroup={nextGroup => {
                                    const nextGroups = [...groups];
                                    nextGroups[index] = nextGroup;
                                    history.commit(nextGroups);
                                }}
                                onEmpty={() => history.commit(groups.filter((_, candidate) => candidate !== index))}
                                fieldOptions={fieldOptions}
                                readOnly={readOnly}
                            />
                        </React.Fragment>
                    ))}

                    {!readOnly && (
                        <Stack alignItems={'center'}>
                            <Button
                                variant={'text'}
                                color={'success'}
                                startIcon={<AddIcon/>}
                                onClick={() => history.commit([...groups, newBox()])}
                            >
                                {t('OR box')}
                            </Button>
                        </Stack>
                    )}
                </Stack>
            )}
        </Stack>
    );
};

export default IfConditionRules;
